import React, { useState } from 'react';
import {
    ChevronRight, ChevronDown, TrendingUp, TrendingDown,
    DollarSign, Target, Eye, MousePointer
} from 'lucide-react';
import { formatNumber, formatCurrency, formatPercent } from '../utils/helpers';
import { CATEGORY_ICONS } from '../constants/placements';
import CreativeCard from './CreativeCard';

const PlacementSection = ({
    category,
    placements,
    creatives,
    creativeAnalysisCache,
    campaignAnalysis,
    expandedCreativeId,
    analyzingCreativeId,
    onToggleCreativeExpand,
    onAnalyzeCreative,
    defaultExpanded = false
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    // Calculate aggregated metrics for this category
    const categoryMetrics = placements.reduce((acc, p) => {
        const m = p.metrics || {};
        return {
            spend: acc.spend + (m.spend || 0),
            revenue: acc.revenue + (m.revenue || 0),
            impressions: acc.impressions + (m.impressions || 0),
            clicks: acc.clicks + (m.clicks || 0)
        };
    }, { spend: 0, revenue: 0, impressions: 0, clicks: 0 });

    const roas = categoryMetrics.spend > 0 ? categoryMetrics.revenue / categoryMetrics.spend : 0;
    const ctr = categoryMetrics.impressions > 0 ? (categoryMetrics.clicks / categoryMetrics.impressions) * 100 : 0;

    // Get unique creatives for this category
    const categoryCreativeIds = new Set(placements.flatMap(p => p.creativeIds || []));
    const categoryCreatives = creatives.filter(c => categoryCreativeIds.has(c.id));

    const categoryIcon = CATEGORY_ICONS[category] || '📍';

    return (
        <div style={{
            border: '1px solid var(--color-border-light)',
            borderRadius: 'var(--radius-lg)',
            marginBottom: 'var(--space-4)',
            overflow: 'hidden',
            background: 'var(--color-surface)'
        }}>
            {/* Section Header */}
            <div
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4)',
                    cursor: 'pointer',
                    background: isExpanded ? 'var(--color-bg-secondary)' : 'transparent',
                    transition: 'background var(--transition-fast)'
                }}
                className="hover-bg"
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    {/* Expand Icon */}
                    {isExpanded ? (
                        <ChevronDown size={20} className="text-secondary" />
                    ) : (
                        <ChevronRight size={20} className="text-secondary" />
                    )}

                    {/* Category Icon & Name */}
                    <span style={{ fontSize: 'var(--font-size-xl)' }}>{categoryIcon}</span>
                    <div>
                        <h4 style={{ fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                            {category}
                        </h4>
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                            {placements.length} placement{placements.length !== 1 ? 's' : ''} • {categoryCreatives.length} creative{categoryCreatives.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                </div>

                {/* Metrics Summary */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-6)'
                }}>
                    {/* Spend */}
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', justifyContent: 'flex-end' }}>
                            <DollarSign size={14} className="text-secondary" />
                            <span style={{ fontWeight: 600 }}>{formatCurrency(categoryMetrics.spend)}</span>
                        </div>
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Spend</span>
                    </div>

                    {/* ROAS */}
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', justifyContent: 'flex-end' }}>
                            <Target size={14} className="text-secondary" />
                            <span style={{
                                fontWeight: 600,
                                color: roas >= 2 ? 'var(--color-success)' : roas >= 1 ? 'var(--color-warning)' : 'var(--color-error)'
                            }}>
                                {roas.toFixed(2)}x
                            </span>
                        </div>
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>ROAS</span>
                    </div>

                    {/* CTR */}
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', justifyContent: 'flex-end' }}>
                            <MousePointer size={14} className="text-secondary" />
                            <span style={{ fontWeight: 600 }}>{ctr.toFixed(2)}%</span>
                        </div>
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>CTR</span>
                    </div>

                    {/* Impressions */}
                    <div style={{ textAlign: 'right', minWidth: '80px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', justifyContent: 'flex-end' }}>
                            <Eye size={14} className="text-secondary" />
                            <span style={{ fontWeight: 600 }}>{formatNumber(categoryMetrics.impressions)}</span>
                        </div>
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Impressions</span>
                    </div>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div style={{
                    padding: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border-light)',
                    background: 'var(--color-bg-secondary)'
                }}>
                    {/* Placement Breakdown Table */}
                    {placements.length > 1 && (
                        <div style={{ marginBottom: 'var(--space-4)' }}>
                            <h5 style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 600,
                                marginBottom: 'var(--space-3)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                Placement Breakdown
                            </h5>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '2fr repeat(4, 1fr)',
                                gap: 'var(--space-2)',
                                fontSize: 'var(--font-size-sm)'
                            }}>
                                {/* Header */}
                                <div style={{ fontWeight: 600, color: 'var(--color-text-secondary)', padding: 'var(--space-2)' }}>Placement</div>
                                <div style={{ fontWeight: 600, color: 'var(--color-text-secondary)', padding: 'var(--space-2)', textAlign: 'right' }}>Spend</div>
                                <div style={{ fontWeight: 600, color: 'var(--color-text-secondary)', padding: 'var(--space-2)', textAlign: 'right' }}>ROAS</div>
                                <div style={{ fontWeight: 600, color: 'var(--color-text-secondary)', padding: 'var(--space-2)', textAlign: 'right' }}>CTR</div>
                                <div style={{ fontWeight: 600, color: 'var(--color-text-secondary)', padding: 'var(--space-2)', textAlign: 'right' }}>Impressions</div>

                                {/* Rows */}
                                {placements.map((placement) => {
                                    const m = placement.metrics || {};
                                    const pRoas = m.spend > 0 ? (m.revenue || 0) / m.spend : 0;
                                    const pCtr = m.impressions > 0 ? ((m.clicks || 0) / m.impressions) * 100 : 0;

                                    return (
                                        <React.Fragment key={placement.placementId}>
                                            <div style={{
                                                padding: 'var(--space-2)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-2)'
                                            }}>
                                                <span>{placement.icon}</span>
                                                <span>{placement.name}</span>
                                            </div>
                                            <div style={{ padding: 'var(--space-2)', textAlign: 'right' }}>
                                                {formatCurrency(m.spend || 0)}
                                            </div>
                                            <div style={{
                                                padding: 'var(--space-2)',
                                                textAlign: 'right',
                                                color: pRoas >= 2 ? 'var(--color-success)' : pRoas >= 1 ? 'var(--color-warning)' : 'var(--color-error)'
                                            }}>
                                                {pRoas.toFixed(2)}x
                                            </div>
                                            <div style={{ padding: 'var(--space-2)', textAlign: 'right' }}>
                                                {pCtr.toFixed(2)}%
                                            </div>
                                            <div style={{ padding: 'var(--space-2)', textAlign: 'right' }}>
                                                {formatNumber(m.impressions || 0)}
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Creatives Grid */}
                    {categoryCreatives.length > 0 ? (
                        <>
                            <h5 style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 600,
                                marginBottom: 'var(--space-3)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                Creatives ({categoryCreatives.length})
                            </h5>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                gap: 'var(--space-4)'
                            }}>
                                {categoryCreatives.map((creative, index) => {
                                    const creativeKey = `${creative.id}-${creative.adSetId || index}`;
                                    return (
                                        <CreativeCard
                                            key={creativeKey}
                                            creative={creative}
                                            analysis={creativeAnalysisCache?.[creative.id]}
                                            isExpanded={expandedCreativeId === creative.id}
                                            isAnalyzing={analyzingCreativeId === creative.id}
                                            onToggleExpand={() => onToggleCreativeExpand?.(creative.id)}
                                            onAnalyze={() => onAnalyzeCreative?.(creative)}
                                            isTopPerformer={campaignAnalysis?.topPerformer?.creativeKey === creativeKey}
                                            recommendation={campaignAnalysis?.recommendations?.find(
                                                r => r.creativeKey === creativeKey
                                            )}
                                        />
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: 'var(--space-6)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            No creatives found for this placement category.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PlacementSection;
